// src/pages/TermsPage.jsx
import React from 'react';
import { FileText } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
        <div className="flex items-center space-x-3 mb-8">
          <FileText className="w-10 h-10 text-blue-600" />
          <h1 className="text-4xl font-bold text-gray-900">Terms of Service</h1>
        </div>

        <div className="prose prose-lg max-w-none space-y-8">
          <p className="text-gray-600 text-lg">
            <strong>Last Updated:</strong> June 5, 2026
          </p>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              By downloading, installing, or using NutriLens Pro ("the App"), you agree to be bound by these Terms of Service.
              If you do not agree to these terms, please do not use the App.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. License</h2>
            <p className="text-gray-700 leading-relaxed">
              We grant you a limited, non-exclusive, non-transferable, revocable license to use NutriLens Pro for personal,
              non-commercial purposes in accordance with these Terms of Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. App Description</h2>
            <p className="text-gray-700 leading-relaxed">
              NutriLens Pro is a barcode scanning and nutrition tracking application that provides nutritional information
              about food products. The app allows you to scan barcodes, search for products, track daily nutrition intake,
              view Nutri-Score ratings, and receive allergen warnings. The app retrieves product data from Open Food Facts,
              a collaborative, free, and open database of food products.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. User Responsibilities</h2>
            <div className="space-y-3">
              <p className="text-gray-700 leading-relaxed">You agree to:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Use the App only for lawful purposes</li>
                <li>Not attempt to reverse engineer, decompile, or disassemble the App</li>
                <li>Not use the App in any way that could damage, disable, or impair our services</li>
                <li>Not attempt to gain unauthorized access to any part of the App</li>
                <li>Comply with all applicable local, state, national, and international laws</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Disclaimer of Warranties</h2>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-4">
              <p className="text-gray-800 font-semibold mb-2">IMPORTANT NOTICE:</p>
              <p className="text-gray-700 leading-relaxed">
                THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. We do not guarantee
                the accuracy, completeness, or reliability of any nutritional information provided through the App.
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Nutritional information is sourced from Open Food Facts and may not always be accurate or up-to-date.
              Always verify information with product packaging and consult healthcare professionals for dietary advice.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              To the maximum extent permitted by law, NutriLens Pro and its developers shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages resulting from:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
              <li>Your use or inability to use the App</li>
              <li>Any inaccurate or incomplete nutritional information</li>
              <li>Any health-related decisions made based on information from the App</li>
              <li>Any dietary or nutritional tracking data provided by the App</li>
              <li>Unauthorized access to or alteration of your data</li>
              <li>Any other matter relating to the App</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Third-Party Content</h2>
            <p className="text-gray-700 leading-relaxed">
              The App displays nutritional information from Open Food Facts, a third-party database. We are not
              responsible for the accuracy, completeness, or reliability of third-party content. The inclusion of
              any links or references to third-party websites does not imply endorsement.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Advertisements</h2>
            <p className="text-gray-700 leading-relaxed">
              The App displays advertisements through Google AdMob. We are not responsible for the content of
              advertisements or the practices of advertisers. Your interaction with advertisements is at your own risk.
              The App's age rating reflects the presence of advertising content.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. In-App Purchases</h2>
            <p className="text-gray-700 leading-relaxed">
              The App offers optional in-app purchases (donations). All purchases are processed through Apple's App Store
              or Google Play Store and are managed by RevenueCat. Purchases are final and non-refundable except
              as required by law or the applicable store's refund policy. Donations are voluntary and do not unlock additional features.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Push Notifications</h2>
            <p className="text-gray-700 leading-relaxed">
              The App may send local push notifications for daily meal reminders and hydration alerts. These notifications
              are optional and can be enabled or disabled at any time in the app settings or through your device's
              notification settings. Notification content is generated locally and does not involve transmission of personal data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Nutrition Tracking</h2>
            <p className="text-gray-700 leading-relaxed">
              The App provides a daily nutrition tracker to help you log and monitor your food intake. All tracking data
              is stored locally on your device. The nutrition tracking feature is intended for general informational and
              wellness purposes only. Calorie counts, macronutrient values, and Nutri-Score ratings are sourced from
              Open Food Facts and may contain inaccuracies. Do not rely solely on this feature for medical dietary management.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Intellectual Property</h2>
            <p className="text-gray-700 leading-relaxed">
              The App and its original content, features, and functionality are owned by NutriLens Pro and are
              protected by international copyright, trademark, and other intellectual property laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Termination</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to terminate or suspend your access to the App immediately, without prior notice
              or liability, for any reason, including breach of these Terms of Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify these Terms of Service at any time. We will notify users of any material
              changes by updating the "Last Updated" date. Your continued use of the App after changes constitutes
              acceptance of the new terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Governing Law</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with applicable laws, without regard to
              conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Medical Disclaimer</h2>
            <div className="bg-red-50 border-l-4 border-red-400 p-4 my-4">
              <p className="text-gray-800 font-semibold mb-2">MEDICAL DISCLAIMER:</p>
              <p className="text-gray-700 leading-relaxed">
                NutriLens Pro is not a medical device and should not be used as a substitute for professional medical
                advice, diagnosis, or treatment. The nutrition tracker, Nutri-Score ratings, allergen warnings, and
                calorie information are for general informational purposes only. Always seek the advice of qualified
                health providers with questions regarding medical conditions, allergies, or dietary requirements.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">17. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed">
              For questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg mt-3">
              <p className="text-gray-800">
                <strong>Email:</strong> <a href="mailto:cyborguser2020@gmail.com" className="text-blue-600 hover:underline">
                  cyborguser2020@gmail.com
                </a>
              </p>
              <p className="text-gray-800 mt-2">
                <strong>App Name:</strong> NutriLens Pro
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
